import React, { useEffect, useMemo, useState } from "react";
import {
  IonActionSheet,
  IonBadge,
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonDatetime,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonRadio,
  IonRadioGroup,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonText,
  IonTextarea,
  IonTitle,
  IonToast,
  IonToolbar,
} from "@ionic/react";
import { closeOutline } from "ionicons/icons";

import Header from "src/components/Header";
import LocationAPI from "src/services/location";
import MarcacionAPI from "src/services/marcacion";
import {
  buscarPuntosServicioRelevamiento,
  crearRelevamientoVisita,
  listarOpcionesRelevamiento,
  listarRelevamientosVisitas,
  obtenerContextoRelevamiento,
  obtenerRelevamientoVisita,
  RelevamientoContexto,
  RelevamientoDetalle,
  RelevamientoPregunta,
  RelevamientoPuntoServicio,
  RelevamientoRegistro,
} from "src/services/relevamientoVisitas";
import { distanciaEntreCoordenadas } from "src/utils/coordenadas";
import { Marcacion, Posicion } from "src/utils/interfaces";

const normalizarFecha = (value: string | string[] | null | undefined) => {
  if (!value) return undefined;
  const raw = Array.isArray(value) ? value[0] : value;
  if (!raw) return undefined;
  return raw.toString().slice(0, 10);
};

type PuntoCercano = Marcacion & { id?: number };

const RelevamientoVisitas: React.FC = () => {
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [cargandoCercanos, setCargandoCercanos] = useState(false);
  const [marcandoPs, setMarcandoPs] = useState<string | null>(null);
  const [toast, setToast] = useState<{ show: boolean; msg: string; color?: string }>({
    show: false,
    msg: "",
  });

  const [preguntas, setPreguntas] = useState<RelevamientoPregunta[]>([]);
  const [modoVisita, setModoVisita] = useState<"cliente" | "prospecto">("cliente");
  const [nombreLugarProspecto, setNombreLugarProspecto] = useState("");
  const [psBusqueda, setPsBusqueda] = useState("");
  const [psBuscando, setPsBuscando] = useState(false);
  const [psResultados, setPsResultados] = useState<RelevamientoPuntoServicio[]>([]);
  const [psSeleccionado, setPsSeleccionado] = useState<RelevamientoPuntoServicio | null>(null);
  const [contexto, setContexto] = useState<RelevamientoContexto | null>(null);
  const [ejecutivo, setEjecutivo] = useState("");
  const [encargado, setEncargado] = useState("");
  const [puestoContacto, setPuestoContacto] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [fechaVisita, setFechaVisita] = useState<string | undefined>(undefined);
  const [respuestas, setRespuestas] = useState<Record<string, string>>({});
  const [historial, setHistorial] = useState<RelevamientoRegistro[]>([]);
  const [listando, setListando] = useState(false);
  const [puedeVerTodas, setPuedeVerTodas] = useState(false);
  const [historialTotal, setHistorialTotal] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [filtroFechaDesde, setFiltroFechaDesde] = useState<string | undefined>(undefined);
  const [filtroFechaHasta, setFiltroFechaHasta] = useState<string | undefined>(undefined);
  const [filtroEjecutivo, setFiltroEjecutivo] = useState("");
  const [filtroPsBusqueda, setFiltroPsBusqueda] = useState("");
  const [filtroPsResultados, setFiltroPsResultados] = useState<RelevamientoPuntoServicio[]>([]);
  const [filtroPsBuscando, setFiltroPsBuscando] = useState(false);
  const [filtroPsSeleccionado, setFiltroPsSeleccionado] = useState<RelevamientoPuntoServicio | null>(null);
  const [detalleAbierto, setDetalleAbierto] = useState<RelevamientoDetalle | null>(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const PAGE_SIZE = 20;
  const [posicion, setPosicion] = useState<Posicion | null>(null);
  const [puntosCercanos, setPuntosCercanos] = useState<PuntoCercano[]>([]);
  const [fechasMarcadas, setFechasMarcadas] = useState<Record<string, string>>({});
  const [ocultarCercanos, setOcultarCercanos] = useState(false);
  const [puntoParaMarcar, setPuntoParaMarcar] = useState<PuntoCercano | null>(null);

  const preguntasOrdenadas = useMemo(
    () => [...preguntas].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0)),
    [preguntas]
  );

  const cargarHistorial = async (
    paginaActual = 0,
    filtros?: {
      fechaDesde?: string;
      fechaHasta?: string;
      psCodigo?: string;
      ejecutivo?: string;
    }
  ) => {
    setListando(true);
    try {
      const ejecutivoArg = filtros?.ejecutivo ?? filtroEjecutivo;
      const respuesta = await listarRelevamientosVisitas({
        ps_codigo: filtros?.psCodigo ?? filtroPsSeleccionado?.ps_codigo,
        fecha_desde: filtros?.fechaDesde ?? filtroFechaDesde,
        fecha_hasta: filtros?.fechaHasta ?? filtroFechaHasta,
        ejecutivo: ejecutivoArg ? ejecutivoArg.trim() : undefined,
        limit: PAGE_SIZE,
        offset: paginaActual * PAGE_SIZE,
      });
      setHistorial(respuesta.datos);
      setHistorialTotal(respuesta.total);
      setPuedeVerTodas(respuesta.puede_ver_todas);
      setPagina(paginaActual);
    } catch (error: any) {
      setToast({ show: true, msg: error?.message || "No se pudo cargar el historial.", color: "warning" });
    } finally {
      setListando(false);
    }
  };

  const abrirDetalle = async (item: RelevamientoRegistro) => {
    setCargandoDetalle(true);
    try {
      const detalle = await obtenerRelevamientoVisita(item.id);
      if (!detalle) {
        setToast({ show: true, msg: "No se pudo obtener el detalle de la visita.", color: "warning" });
        return;
      }
      setDetalleAbierto(detalle);
    } catch (error: any) {
      setToast({ show: true, msg: error?.message || "No se pudo obtener el detalle.", color: "danger" });
    } finally {
      setCargandoDetalle(false);
    }
  };

  const aplicarFiltros = () => {
    cargarHistorial(0);
  };

  const limpiarFiltros = () => {
    setFiltroFechaDesde(undefined);
    setFiltroFechaHasta(undefined);
    setFiltroPsSeleccionado(null);
    setFiltroPsBusqueda("");
    setFiltroPsResultados([]);
    setFiltroEjecutivo("");
    cargarHistorial(0, {
      fechaDesde: undefined,
      fechaHasta: undefined,
      psCodigo: undefined,
      ejecutivo: "",
    });
  };

  const cargarPuntosCercanos = async () => {
    setCargandoCercanos(true);
    try {
      const ubicacion = await LocationAPI.getLocation();
      const latitud = ubicacion.coords.latitude;
      const longitud = ubicacion.coords.longitude;
      setPosicion({ latitud, longitud });

      const response = await MarcacionAPI.puntoDeServicioCercanos(latitud, longitud);
      const puntos = (response?.data?.datos || []).map((punto: Marcacion) => ({
        ...punto,
        distancia: distanciaEntreCoordenadas(latitud, longitud, punto.ps_latitud, punto.ps_longitud),
      }));
      puntos.sort((a: PuntoCercano, b: PuntoCercano) => a.distancia - b.distancia);
      setPuntosCercanos(puntos);
    } catch (error: any) {
      setPuntosCercanos([]);
      setToast({
        show: true,
        msg: error?.response?.data?.error || error?.message || "No se pudieron cargar los puntos cercanos.",
        color: "warning",
      });
    } finally {
      setCargandoCercanos(false);
    }
  };

  useEffect(() => {
    let activo = true;
    const cargar = async () => {
      setCargando(true);
      try {
        const data = await listarOpcionesRelevamiento();
        if (!activo) return;
        setPreguntas(data?.preguntas || []);
        await cargarHistorial();
        await cargarPuntosCercanos();
      } catch (error: any) {
        if (!activo) return;
        setToast({
          show: true,
          msg: error?.message || "No se pudo cargar Visitas Comercial.",
          color: "danger",
        });
      } finally {
        if (activo) setCargando(false);
      }
    };
    cargar();
    return () => {
      activo = false;
    };
  }, []);

  useEffect(() => {
    let activo = true;
    const termino = filtroPsBusqueda.trim();

    if (filtroPsSeleccionado && termino === filtroPsSeleccionado.ps_nombre) {
      setFiltroPsResultados([]);
      setFiltroPsBuscando(false);
      return;
    }

    if (filtroPsSeleccionado && termino !== filtroPsSeleccionado.ps_nombre) {
      setFiltroPsSeleccionado(null);
    }

    if (termino.length < 2) {
      setFiltroPsResultados([]);
      setFiltroPsBuscando(false);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      setFiltroPsBuscando(true);
      try {
        const datos = await buscarPuntosServicioRelevamiento(termino);
        if (!activo) return;
        setFiltroPsResultados((datos || []).slice(0, 8));
      } catch {
        if (!activo) return;
        setFiltroPsResultados([]);
      } finally {
        if (activo) setFiltroPsBuscando(false);
      }
    }, 250);

    return () => {
      activo = false;
      window.clearTimeout(timeoutId);
    };
  }, [filtroPsBusqueda, filtroPsSeleccionado]);

  useEffect(() => {
    let activo = true;
    const termino = psBusqueda.trim();

    if (psSeleccionado && termino === psSeleccionado.ps_nombre) {
      setPsResultados([]);
      setPsBuscando(false);
      return;
    }

    if (psSeleccionado && termino !== psSeleccionado.ps_nombre) {
      setPsSeleccionado(null);
      setContexto(null);
      setEjecutivo("");
      setEncargado("");
      setPuestoContacto("");
    }

    if (termino.length < 2) {
      setPsResultados([]);
      setPsBuscando(false);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      setPsBuscando(true);
      try {
        const datos = await buscarPuntosServicioRelevamiento(termino);
        if (!activo) return;
        setPsResultados((datos || []).slice(0, 8));
      } catch {
        if (!activo) return;
        setPsResultados([]);
      } finally {
        if (activo) setPsBuscando(false);
      }
    }, 250);

    return () => {
      activo = false;
      window.clearTimeout(timeoutId);
    };
  }, [psBusqueda, psSeleccionado]);

  const seleccionarPunto = async (ps: Pick<RelevamientoPuntoServicio, "ps_nombre" | "ps_codigo"> & { id?: number }) => {
    setPsResultados([]);
    setPsBuscando(false);
    setOcultarCercanos(true);
    setPsBusqueda(ps.ps_nombre);
    setPsSeleccionado({
      id: ps.id ?? 0,
      ps_nombre: ps.ps_nombre,
      ps_codigo: ps.ps_codigo,
    });
    setEncargado("");
    setPuestoContacto("");
    setFechaVisita(fechasMarcadas[ps.ps_codigo] || undefined);
    try {
      const data = await obtenerContextoRelevamiento(
        ps.id ? { psId: ps.id } : { psCodigo: ps.ps_codigo }
      );
      const puntoResuelto = {
        id: Number(data?.ps?.id || ps.id || 0),
        ps_nombre: String(data?.ps?.nombre || ps.ps_nombre || ""),
        ps_codigo: String(data?.ps?.codigo || ps.ps_codigo || ""),
      };
      setPsSeleccionado(puntoResuelto);
      setPsBusqueda(puntoResuelto.ps_nombre);
      setContexto(data);
      setEjecutivo(String(data?.ejecutivo?.nombre || ""));
      setEncargado(String(data?.contacto?.nombre || ""));
    } catch (error: any) {
      setPsSeleccionado(null);
      setContexto(null);
      setEjecutivo("");
      setEncargado("");
      setPuestoContacto("");
      setToast({ show: true, msg: error?.message || "No se pudo obtener el contexto del punto.", color: "danger" });
    }
  };

  const seleccionarPuntoCercano = async (punto: PuntoCercano) => {
    setOcultarCercanos(true);
    await seleccionarPunto({
      ps_nombre: punto.ps_nombre,
      ps_codigo: punto.ps_codigo,
    });
  };

  const marcarPuntoCercano = async (punto: PuntoCercano, tipo: "entrada" | "salida") => {
    if (!posicion) {
      setToast({ show: true, msg: "No se pudo obtener la ubicación actual.", color: "warning" });
      return;
    }

    setMarcandoPs(punto.ps_codigo);
    try {
      const response = tipo === "entrada"
        ? await MarcacionAPI.marcarEntrada(punto.ps_codigo, posicion)
        : await MarcacionAPI.marcarSalida(punto.ps_codigo, posicion);
      const fechaMarcacion = normalizarFecha(response?.data?.fecha_marcacion);
      if (fechaMarcacion) {
        setFechasMarcadas((prev) => ({ ...prev, [punto.ps_codigo]: fechaMarcacion }));
        if (psSeleccionado?.ps_codigo === punto.ps_codigo) {
          setFechaVisita(fechaMarcacion);
        }
      }
      setToast({
        show: true,
        msg: response?.data?.info || "Marcacion registrada correctamente.",
        color: "success",
      });
    } catch (error: any) {
      setToast({
        show: true,
        msg: error?.response?.data?.error || error?.message || "No se pudo registrar la marcacion.",
        color: "danger",
      });
    } finally {
      setMarcandoPs(null);
    }
  };

  const actualizarRespuesta = (codigo: string, valor: string) => {
    setRespuestas((prev) => ({ ...prev, [codigo]: valor }));
  };

  const limpiarFormulario = () => {
    setPsBusqueda("");
    setPsResultados([]);
    setPsSeleccionado(null);
    setContexto(null);
    setEjecutivo("");
    setEncargado("");
    setPuestoContacto("");
    setObservaciones("");
    setFechaVisita(undefined);
    setRespuestas({});
    setOcultarCercanos(false);
    setNombreLugarProspecto("");
    setModoVisita("cliente");
  };

  const cambiarModoVisita = (modo: "cliente" | "prospecto") => {
    if (modo === modoVisita) return;
    setModoVisita(modo);
    setPsBusqueda("");
    setPsResultados([]);
    setPsSeleccionado(null);
    setContexto(null);
    setEjecutivo("");
    setEncargado("");
    setPuestoContacto("");
    setNombreLugarProspecto("");
    setOcultarCercanos(modo === "prospecto");
  };

  const guardar = async () => {
    const esProspecto = modoVisita === "prospecto";

    if (esProspecto) {
      if (!nombreLugarProspecto.trim()) {
        setToast({ show: true, msg: "Debe indicar el nombre del lugar visitado.", color: "warning" });
        return;
      }
    } else if (!psSeleccionado?.id) {
      setToast({ show: true, msg: "Debe seleccionar un punto de servicio.", color: "warning" });
      return;
    }
    if (!encargado.trim()) {
      setToast({ show: true, msg: "Debe completar el Contacto del Cliente/Punto de Servicio/Cuenta.", color: "warning" });
      return;
    }
    if (!puestoContacto.trim()) {
      setToast({ show: true, msg: "Debe completar el Puesto del Contacto.", color: "warning" });
      return;
    }

    setGuardando(true);
    try {
      const payload: any = {
        ejecutivo,
        encargado,
        puesto_contacto: puestoContacto,
        observaciones,
        fecha_visita: fechaVisita,
        respuestas: preguntasOrdenadas.map((pregunta) => ({
          codigo: pregunta.codigo,
          respuesta: respuestas[pregunta.codigo] ?? "",
        })),
      };
      if (esProspecto) {
        payload.es_prospecto = true;
        payload.nombre_lugar = nombreLugarProspecto.trim();
      } else {
        payload.ps_id = psSeleccionado!.id;
      }
      await crearRelevamientoVisita(payload);
      setToast({ show: true, msg: "Visita registrada correctamente.", color: "success" });
      limpiarFormulario();
      await cargarHistorial();
    } catch (error: any) {
      setToast({ show: true, msg: error?.message || "No se pudo guardar la visita.", color: "danger" });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <IonPage>
      <Header titulo="Visitas Comercial" />
      <IonContent id="relevamiento-visitas" className="ion-padding">
        {cargando ? (
          <div style={{ display: "flex", justifyContent: "center", paddingTop: 32 }}>
            <IonSpinner name="crescent" />
          </div>
        ) : (
          <>
            <IonCard>
              <IonCardHeader>
                <IonCardTitle>Tipo de visita</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonSegment
                  value={modoVisita}
                  onIonChange={(e) =>
                    cambiarModoVisita(((e.detail.value as string) || "cliente") as "cliente" | "prospecto")
                  }
                >
                  <IonSegmentButton value="cliente">
                    <IonLabel>Cliente existente</IonLabel>
                  </IonSegmentButton>
                  <IonSegmentButton value="prospecto">
                    <IonLabel>Prospecto / No cliente</IonLabel>
                  </IonSegmentButton>
                </IonSegment>
                {modoVisita === "prospecto" ? (
                  <IonItem>
                    <IonLabel position="stacked">Nombre del lugar visitado *</IonLabel>
                    <IonInput
                      value={nombreLugarProspecto}
                      placeholder="Razón social o nombre comercial"
                      onIonChange={(e) => setNombreLugarProspecto(String(e.detail.value || ""))}
                    />
                  </IonItem>
                ) : null}
              </IonCardContent>
            </IonCard>

            {modoVisita === "cliente" ? (
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle>Buscar punto de servicio</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonItem>
                    <IonLabel position="stacked">Buscar punto de servicio</IonLabel>
                    <IonInput
                      value={psBusqueda}
                      placeholder="Ingrese nombre del punto"
                      onIonChange={(e) => setPsBusqueda(String(e.detail.value || ""))}
                    />
                  </IonItem>
                  <div style={{ display: "flex", gap: 12, paddingTop: 12 }}>
                    {psBuscando ? <IonSpinner name="crescent" /> : null}
                    {psSeleccionado?.id ? <IonBadge color="success">{psSeleccionado.ps_codigo}</IonBadge> : null}
                    {psSeleccionado?.ps_codigo && fechasMarcadas[psSeleccionado.ps_codigo] ? (
                      <IonBadge color="tertiary">Marcado {fechasMarcadas[psSeleccionado.ps_codigo]}</IonBadge>
                    ) : null}
                  </div>

                  {!psSeleccionado && psResultados.length > 0 ? (
                    <IonList>
                      {psResultados.map((ps) => (
                        <IonItem button key={ps.id} onClick={() => seleccionarPunto(ps)}>
                          <IonLabel>
                            <h2>{ps.ps_nombre}</h2>
                            <p>{ps.ps_codigo}</p>
                          </IonLabel>
                        </IonItem>
                      ))}
                    </IonList>
                  ) : null}
                </IonCardContent>
              </IonCard>
            ) : null}

            {modoVisita === "cliente" && !ocultarCercanos && (
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle>Puntos cercanos</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  {cargandoCercanos ? (
                    <IonSpinner name="crescent" />
                  ) : puntosCercanos.length === 0 ? (
                    <IonText color="medium">No se encontraron puntos cercanos para tu ubicacion actual.</IonText>
                  ) : (
                    <IonList>
                      {puntosCercanos.map((punto) => (
                        <IonItem key={`${punto.ps_codigo}-${punto.ps_nombre}`}>
                          <div style={{ width: "100%", padding: "8px 0" }}>
                            <div>
                              <strong>{punto.ps_nombre}</strong>
                              <p style={{ margin: "2px 0", fontSize: 13, color: "var(--ion-color-medium)" }}>{punto.ps_codigo}</p>
                              <p style={{ margin: "2px 0", fontSize: 13, color: "var(--ion-color-medium)" }}>A unos {Math.round(punto.distancia)} metros</p>
                            </div>
                            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                              <IonButton
                                size="small"
                                disabled={marcandoPs === punto.ps_codigo}
                                onClick={() => setPuntoParaMarcar(punto)}
                              >
                                {marcandoPs === punto.ps_codigo ? <IonSpinner name="crescent" /> : "Marcar"}
                              </IonButton>
                              <IonButton
                                fill="outline"
                                size="small"
                                onClick={() => seleccionarPuntoCercano(punto)}
                              >
                                Realizar Evaluacion
                              </IonButton>
                            </div>
                          </div>
                        </IonItem>
                      ))}
                    </IonList>
                  )}
                </IonCardContent>
              </IonCard>
            )}

            <IonCard>
              <IonCardHeader>
                <IonCardTitle>Nueva Visita</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                {(modoVisita === "cliente" && psSeleccionado?.id) ||
                (modoVisita === "prospecto" && nombreLugarProspecto.trim()) ? (
                  <>
                    <div style={{ display: "flex", gap: 12, paddingBottom: 12 }}>
                      {modoVisita === "prospecto" ? (
                        <IonBadge color="warning">Prospecto</IonBadge>
                      ) : (
                        <>
                          <IonBadge color="success">{psSeleccionado!.ps_codigo}</IonBadge>
                          {fechasMarcadas[psSeleccionado!.ps_codigo] ? (
                            <IonBadge color="tertiary">
                              Marcado {fechasMarcadas[psSeleccionado!.ps_codigo]}
                            </IonBadge>
                          ) : null}
                        </>
                      )}
                    </div>
                    <IonItem>
                      <IonLabel position="stacked">Ejecutivo</IonLabel>
                      <IonInput
                        value={ejecutivo}
                        onIonChange={(e) => setEjecutivo(String(e.detail.value || ""))}
                      />
                    </IonItem>
                    <IonItem>
                      <IonLabel position="stacked">Contacto del Cliente/Punto de Servicio/Cuenta *</IonLabel>
                      <IonInput
                        value={encargado}
                        onIonChange={(e) => setEncargado(String(e.detail.value || ""))}
                      />
                    </IonItem>
                    <IonItem>
                      <IonLabel position="stacked">Puesto del Contacto *</IonLabel>
                      <IonInput
                        value={puestoContacto}
                        onIonChange={(e) => setPuestoContacto(String(e.detail.value || ""))}
                      />
                    </IonItem>
                    <IonItem>
                      <IonLabel position="stacked">Fecha de visita</IonLabel>
                      <IonDatetime
                        displayFormat="DD/MM/YYYY"
                        pickerFormat="DD/MM/YYYY"
                        cancelText="Cancelar"
                        doneText="Listo"
                        value={fechaVisita}
                        onIonChange={(e) => setFechaVisita(normalizarFecha(e.detail.value))}
                      />
                    </IonItem>

                    {preguntasOrdenadas.map((pregunta) => (
                      <div key={pregunta.id} style={{ paddingTop: 16 }}>
                        <IonText>
                          <h3 style={{ marginBottom: 8 }}>{pregunta.texto}</h3>
                        </IonText>
                        {pregunta.opciones && pregunta.opciones.length > 0 ? (
                          <IonRadioGroup
                            value={respuestas[pregunta.codigo] || ""}
                            onIonChange={(e) => actualizarRespuesta(pregunta.codigo, String(e.detail.value || ""))}
                          >
                            {pregunta.opciones.map((opcion) => (
                              <IonItem key={`${pregunta.codigo}-${opcion.codigo}`}>
                                <IonLabel>{opcion.etiqueta}</IonLabel>
                                <IonRadio slot="start" value={opcion.codigo} />
                              </IonItem>
                            ))}
                          </IonRadioGroup>
                        ) : (
                          <IonItem>
                            <IonInput
                              value={respuestas[pregunta.codigo] || ""}
                              onIonChange={(e) => actualizarRespuesta(pregunta.codigo, String(e.detail.value || ""))}
                            />
                          </IonItem>
                        )}
                      </div>
                    ))}

                    <IonItem>
                      <IonLabel position="stacked">Observaciones</IonLabel>
                      <IonTextarea
                        autoGrow
                        value={observaciones}
                        onIonChange={(e) => setObservaciones(String(e.detail.value || ""))}
                      />
                    </IonItem>

                    <div style={{ display: "flex", gap: 12, paddingTop: 20 }}>
                      <IonButton onClick={guardar} disabled={guardando}>
                        {guardando ? <IonSpinner name="crescent" /> : "Guardar"}
                      </IonButton>
                      <IonButton fill="outline" onClick={limpiarFormulario} disabled={guardando}>
                        Limpiar
                      </IonButton>
                    </div>
                  </>
                ) : null}
              </IonCardContent>
            </IonCard>

            <IonCard>
              <IonCardHeader>
                <IonCardTitle>{puedeVerTodas ? "Visitas registradas" : "Mis visitas"}</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonItem>
                  <IonLabel position="stacked">Punto de servicio</IonLabel>
                  <IonInput
                    value={filtroPsBusqueda}
                    placeholder="Filtrar por punto de servicio"
                    onIonChange={(e) => setFiltroPsBusqueda(String(e.detail.value || ""))}
                  />
                </IonItem>
                {filtroPsBuscando ? <IonSpinner name="crescent" /> : null}
                {!filtroPsSeleccionado && filtroPsResultados.length > 0 ? (
                  <IonList>
                    {filtroPsResultados.map((ps) => (
                      <IonItem
                        button
                        key={`f-${ps.id}`}
                        onClick={() => {
                          setFiltroPsSeleccionado(ps);
                          setFiltroPsBusqueda(ps.ps_nombre);
                          setFiltroPsResultados([]);
                        }}
                      >
                        <IonLabel>
                          <h2>{ps.ps_nombre}</h2>
                          <p>{ps.ps_codigo}</p>
                        </IonLabel>
                      </IonItem>
                    ))}
                  </IonList>
                ) : null}
                {filtroPsSeleccionado ? (
                  <div style={{ paddingTop: 8 }}>
                    <IonBadge color="success">{filtroPsSeleccionado.ps_codigo}</IonBadge>
                  </div>
                ) : null}

                {puedeVerTodas ? (
                  <IonItem>
                    <IonLabel position="stacked">Ejecutivo</IonLabel>
                    <IonInput
                      value={filtroEjecutivo}
                      placeholder="Filtrar por nombre del ejecutivo"
                      onIonChange={(e) => setFiltroEjecutivo(String(e.detail.value || ""))}
                    />
                  </IonItem>
                ) : null}

                <IonItem>
                  <IonLabel position="stacked">Desde</IonLabel>
                  <IonDatetime
                    displayFormat="DD/MM/YYYY"
                    pickerFormat="DD/MM/YYYY"
                    cancelText="Cancelar"
                    doneText="Listo"
                    value={filtroFechaDesde}
                    onIonChange={(e) => setFiltroFechaDesde(normalizarFecha(e.detail.value))}
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">Hasta</IonLabel>
                  <IonDatetime
                    displayFormat="DD/MM/YYYY"
                    pickerFormat="DD/MM/YYYY"
                    cancelText="Cancelar"
                    doneText="Listo"
                    value={filtroFechaHasta}
                    onIonChange={(e) => setFiltroFechaHasta(normalizarFecha(e.detail.value))}
                  />
                </IonItem>

                <div style={{ display: "flex", gap: 8, paddingTop: 12 }}>
                  <IonButton size="small" onClick={aplicarFiltros} disabled={listando}>
                    Aplicar
                  </IonButton>
                  <IonButton size="small" fill="outline" onClick={limpiarFiltros} disabled={listando}>
                    Limpiar
                  </IonButton>
                </div>

                {listando ? (
                  <div style={{ paddingTop: 16 }}>
                    <IonSpinner name="crescent" />
                  </div>
                ) : historial.length === 0 ? (
                  <div style={{ paddingTop: 16 }}>
                    <IonText color="medium">No se encontraron visitas con los filtros aplicados.</IonText>
                  </div>
                ) : (
                  <>
                    <IonList>
                      {historial.map((item) => {
                        const esProspecto = Boolean(item.es_prospecto);
                        return (
                          <IonItem button key={item.id} onClick={() => abrirDetalle(item)}>
                            <IonLabel>
                              <h2>
                                {item.ps_nombre}
                                {esProspecto ? (
                                  <IonBadge color="warning" style={{ marginLeft: 8 }}>
                                    Prospecto
                                  </IonBadge>
                                ) : null}
                              </h2>
                              <p>{item.fecha_visita || item.fecha_creacion}</p>
                              <p>{item.ejecutivo || "Sin ejecutivo"}</p>
                              {puedeVerTodas && item.creado_por_usuario ? (
                                <p style={{ fontSize: 12, color: "var(--ion-color-medium)" }}>
                                  Registrado por: {item.creado_por_usuario}
                                  {item.creado_por_cargo ? ` (${item.creado_por_cargo})` : ""}
                                </p>
                              ) : null}
                            </IonLabel>
                            {item.ps_codigo ? (
                              <IonBadge slot="end" color="medium">{item.ps_codigo}</IonBadge>
                            ) : null}
                          </IonItem>
                        );
                      })}
                    </IonList>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        paddingTop: 12,
                      }}
                    >
                      <IonText color="medium" style={{ fontSize: 13 }}>
                        {historialTotal === 0
                          ? "Sin resultados"
                          : `Mostrando ${pagina * PAGE_SIZE + 1}-${Math.min(
                              (pagina + 1) * PAGE_SIZE,
                              historialTotal
                            )} de ${historialTotal}`}
                      </IonText>
                      <div style={{ display: "flex", gap: 8 }}>
                        <IonButton
                          size="small"
                          fill="outline"
                          disabled={listando || pagina === 0}
                          onClick={() => cargarHistorial(Math.max(0, pagina - 1))}
                        >
                          Anterior
                        </IonButton>
                        <IonButton
                          size="small"
                          fill="outline"
                          disabled={
                            listando || (pagina + 1) * PAGE_SIZE >= historialTotal
                          }
                          onClick={() => cargarHistorial(pagina + 1)}
                        >
                          Siguiente
                        </IonButton>
                      </div>
                    </div>
                  </>
                )}
              </IonCardContent>
            </IonCard>
          </>
        )}

        <IonActionSheet
          isOpen={puntoParaMarcar !== null}
          header={puntoParaMarcar?.ps_nombre}
          onDidDismiss={() => setPuntoParaMarcar(null)}
          buttons={[
            {
              text: "Marcar Entrada",
              handler: () => {
                if (puntoParaMarcar) marcarPuntoCercano(puntoParaMarcar, "entrada");
              },
            },
            {
              text: "Marcar Salida",
              handler: () => {
                if (puntoParaMarcar) marcarPuntoCercano(puntoParaMarcar, "salida");
              },
            },
            {
              text: "Cancelar",
              role: "cancel",
            },
          ]}
        />

        <IonModal isOpen={detalleAbierto !== null} onDidDismiss={() => setDetalleAbierto(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Detalle de visita</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setDetalleAbierto(null)}>
                  <IonIcon slot="icon-only" icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {cargandoDetalle ? (
              <IonSpinner name="crescent" />
            ) : detalleAbierto ? (
              <>
                <IonItem lines="none">
                  <IonLabel>
                    <h2>
                      {detalleAbierto.ps_nombre}
                      {detalleAbierto.es_prospecto ? (
                        <IonBadge color="warning" style={{ marginLeft: 8 }}>
                          Prospecto
                        </IonBadge>
                      ) : null}
                    </h2>
                    {detalleAbierto.ps_codigo ? <p>{detalleAbierto.ps_codigo}</p> : null}
                  </IonLabel>
                </IonItem>
                <IonItem lines="none">
                  <IonLabel>
                    <p><strong>Fecha de visita:</strong> {detalleAbierto.fecha_visita || "-"}</p>
                    <p><strong>Ejecutivo:</strong> {detalleAbierto.ejecutivo || "-"}</p>
                    <p><strong>Contacto del Cliente/Punto de Servicio/Cuenta:</strong> {detalleAbierto.encargado || "-"}</p>
                    <p><strong>Puesto del Contacto:</strong> {detalleAbierto.puesto_contacto || "-"}</p>
                    {detalleAbierto.creado_por_usuario ? (
                      <p>
                        <strong>Registrado por:</strong> {detalleAbierto.creado_por_usuario}
                        {detalleAbierto.creado_por_cargo ? ` (${detalleAbierto.creado_por_cargo})` : ""}
                      </p>
                    ) : null}
                    {detalleAbierto.fecha_creacion ? (
                      <p><strong>Creado:</strong> {detalleAbierto.fecha_creacion}</p>
                    ) : null}
                  </IonLabel>
                </IonItem>

                <div style={{ paddingTop: 12 }}>
                  <IonText><h3>Respuestas</h3></IonText>
                </div>
                {(detalleAbierto.respuestas || []).length === 0 ? (
                  <IonText color="medium">No se registraron respuestas.</IonText>
                ) : (
                  <IonList>
                    {(detalleAbierto.respuestas || []).map((r) => (
                      <IonItem key={r.id}>
                        <IonLabel className="ion-text-wrap">
                          <h3>{r.pregunta_texto}</h3>
                          <p>{r.opcion_etiqueta}</p>
                        </IonLabel>
                      </IonItem>
                    ))}
                  </IonList>
                )}

                {detalleAbierto.observaciones ? (
                  <>
                    <div style={{ paddingTop: 12 }}>
                      <IonText><h3>Observaciones</h3></IonText>
                    </div>
                    <IonItem lines="none">
                      <IonLabel className="ion-text-wrap">
                        <p>{detalleAbierto.observaciones}</p>
                      </IonLabel>
                    </IonItem>
                  </>
                ) : null}
              </>
            ) : null}
          </IonContent>
        </IonModal>

        <IonToast
          isOpen={toast.show}
          message={toast.msg}
          color={toast.color}
          duration={3500}
          onDidDismiss={() => setToast((prev) => ({ ...prev, show: false }))}
        />
      </IonContent>
    </IonPage>
  );
};

export default RelevamientoVisitas;
